import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Admin.css';

function Admin() {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState('');
  const [sessionIdConfigured, setSessionIdConfigured] = useState(false);
  const [odooBaseUrl, setOdooBaseUrl] = useState('');
  const [externalApiTargets, setExternalApiTargets] = useState([
    { id: 'default', label: 'Default', baseUrl: '', bearerTokenInput: '', bearerTokenConfigured: false, enabled: true }
  ]);
  const [apiKey, setApiKey] = useState('');
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [generatedApiKey, setGeneratedApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [syncStats, setSyncStats] = useState({ synced: 0, total: 0 });
  const [picList, setPicList] = useState([]);
  const [newPicName, setNewPicName] = useState('');
  const [editingPic, setEditingPic] = useState(null);
  const [picMessage, setPicMessage] = useState({ type: '', text: '' });
  const [vendorList, setVendorList] = useState([]);
  const [vendorMessage, setVendorMessage] = useState({ type: '', text: '' });
  const [newVendorName, setNewVendorName] = useState('');
  const [newVendorDigitCount, setNewVendorDigitCount] = useState('');
  const [editingVendor, setEditingVendor] = useState(null);
  const [wmsApiBaseUrl, setWmsApiBaseUrl] = useState('https://wms.foom.id/api');
  const [wmsAccessTokenInput, setWmsAccessTokenInput] = useState('');
  const [wmsAccessTokenConfigured, setWmsAccessTokenConfigured] = useState(false);
  const [wmsUsername, setWmsUsername] = useState('');
  const [wmsCompanyId, setWmsCompanyId] = useState('FOOM');
  const [wmsSite, setWmsSite] = useState('PROD');
  const [reconcilePreview, setReconcilePreview] = useState(null);

  useEffect(() => {
    fetchConfig();
    fetchPicList();
    fetchVendorList();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get('/api/admin/config');
      if (response.data.success) {
        setSessionId('');
        setSessionIdConfigured(!!response.data.config.sessionIdConfigured);
        setOdooBaseUrl(response.data.config.odooBaseUrl || '');
        const targetsFromApi = response.data.config.externalApiTargets;
        if (Array.isArray(targetsFromApi) && targetsFromApi.length > 0) {
          setExternalApiTargets(
            targetsFromApi.map((t) => ({
              id: t.id || 'default',
              label: t.label || t.id || '',
              baseUrl: t.baseUrl || '',
              bearerTokenInput: '',
              bearerTokenConfigured: !!t.bearerTokenConfigured,
              enabled: t.enabled !== false
            }))
          );
        } else {
          setExternalApiTargets([
            {
              id: 'default',
              label: 'Default',
              baseUrl: response.data.config.externalApiBaseUrl || '',
              bearerTokenInput: '',
              bearerTokenConfigured: !!response.data.config.externalApiBearerTokenConfigured,
              enabled: true
            }
          ]);
        }
        setApiKey(response.data.config.apiKey || '');
        setApiKeyConfigured(response.data.config.apiKeyConfigured || false);
        setWmsApiBaseUrl(response.data.config.wmsApiBaseUrl || 'https://wms.foom.id/api');
        setWmsAccessTokenInput('');
        setWmsAccessTokenConfigured(!!response.data.config.wmsAccessTokenConfigured);
        setWmsUsername(response.data.config.wmsUsername || '');
        setWmsCompanyId(response.data.config.wmsCompanyId || 'FOOM');
        setWmsSite(response.data.config.wmsSite || 'PROD');
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      setMessage({ type: 'error', text: 'Failed to load configuration' });
    }
  };

  const fetchPicList = async () => {
    try {
      const response = await axios.get('/api/pic/all');
      if (response.data.success) {
        setPicList(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching PIC list:', error);
    }
  };

  const fetchVendorList = async () => {
    try {
      const response = await axios.get('/api/authenticity-vendors/all');
      if (response.data.success) {
        setVendorList(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching vendor list:', error);
    }
  };

  const handleAddVendor = async () => {
    if (!newVendorName.trim()) {
      setVendorMessage({ type: 'error', text: 'Nama vendor tidak boleh kosong' });
      return;
    }
    const dc = parseInt(String(newVendorDigitCount), 10);
    if (Number.isNaN(dc) || dc < 1) {
      setVendorMessage({ type: 'error', text: 'Jumlah digit harus bilangan bulat positif' });
      return;
    }
    try {
      const response = await axios.post('/api/authenticity-vendors', {
        name: newVendorName.trim(),
        digit_count: dc,
        is_active: 1
      });
      if (response.data.success) {
        setVendorMessage({ type: 'success', text: response.data.message || 'Vendor ditambahkan' });
        setNewVendorName('');
        setNewVendorDigitCount('');
        fetchVendorList();
      } else {
        setVendorMessage({ type: 'error', text: response.data.error || 'Gagal menambah vendor' });
      }
    } catch (error) {
      setVendorMessage({ type: 'error', text: error.response?.data?.error || 'Gagal menambah vendor' });
    }
  };

  const handleUpdateVendor = async (id, name, digit_count, is_active) => {
    try {
      const response = await axios.put(`/api/authenticity-vendors/${id}`, { name, digit_count, is_active });
      if (response.data.success) {
        setVendorMessage({ type: 'success', text: response.data.message || 'Vendor diperbarui' });
        setEditingVendor(null);
        fetchVendorList();
      } else {
        setVendorMessage({ type: 'error', text: response.data.error || 'Gagal update vendor' });
      }
    } catch (error) {
      setVendorMessage({ type: 'error', text: error.response?.data?.error || 'Gagal update vendor' });
    }
  };

  const handleDeactivateVendor = async (id) => {
    if (!window.confirm('Nonaktifkan vendor ini? Vendor tidak akan muncul di dropdown produksi.')) {
      return;
    }
    try {
      const response = await axios.delete(`/api/authenticity-vendors/${id}`);
      if (response.data.success) {
        setVendorMessage({ type: 'success', text: response.data.message || 'Vendor dinonaktifkan' });
        fetchVendorList();
      } else {
        setVendorMessage({ type: 'error', text: response.data.error || 'Gagal' });
      }
    } catch (error) {
      setVendorMessage({ type: 'error', text: error.response?.data?.error || 'Gagal' });
    }
  };

  const handleAddPic = async () => {
    if (!newPicName.trim()) {
      setPicMessage({ type: 'error', text: 'Nama PIC tidak boleh kosong' });
      return;
    }

    try {
      const response = await axios.post('/api/pic/add', { name: newPicName.trim() });
      if (response.data.success) {
        setPicMessage({ type: 'success', text: 'PIC berhasil ditambahkan' });
        setNewPicName('');
        fetchPicList();
      } else {
        setPicMessage({ type: 'error', text: response.data.error || 'Gagal menambahkan PIC' });
      }
    } catch (error) {
      setPicMessage({ type: 'error', text: error.response?.data?.error || 'Gagal menambahkan PIC' });
    }
  };

  const handleUpdatePic = async (id, name, is_active) => {
    try {
      const response = await axios.put(`/api/pic/update/${id}`, { name, is_active });
      if (response.data.success) {
        setPicMessage({ type: 'success', text: 'PIC berhasil diupdate' });
        setEditingPic(null);
        fetchPicList();
      } else {
        setPicMessage({ type: 'error', text: response.data.error || 'Gagal mengupdate PIC' });
      }
    } catch (error) {
      setPicMessage({ type: 'error', text: error.response?.data?.error || 'Gagal mengupdate PIC' });
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleDeletePic = async (id) => {
    if (!window.confirm('Apakah Anda yakin ingin menonaktifkan PIC ini?')) {
      return;
    }

    try {
      const response = await axios.delete(`/api/pic/delete/${id}`);
      if (response.data.success) {
        setPicMessage({ type: 'success', text: 'PIC berhasil dinonaktifkan' });
        fetchPicList();
      } else {
        setPicMessage({ type: 'error', text: response.data.error || 'Gagal menonaktifkan PIC' });
      }
    } catch (error) {
      setPicMessage({ type: 'error', text: error.response?.data?.error || 'Gagal menonaktifkan PIC' });
    }
  };

  const handleTogglePicStatus = async (id, currentStatus) => {
    const pic = picList.find(p => p.id === id);
    if (!pic) return;
    
    const newStatus = currentStatus === 1 ? 0 : 1;
    await handleUpdatePic(id, pic.name, newStatus);
  };

  const handleGenerateApiKey = async () => {
    if (!window.confirm('Generate new API key? This will replace the existing API key if one exists. Make sure to save the new key securely!')) {
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post('/api/admin/generate-api-key');
      if (response.data.success) {
        setGeneratedApiKey(response.data.apiKey);
        setShowApiKey(true);
        setApiKeyConfigured(true);
        setMessage({ type: 'success', text: 'API key generated successfully! Please save it securely - it will not be shown again.' });
        // Refresh config to get masked key
        fetchConfig();
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Failed to generate API key' });
      }
    } catch (error) {
      console.error('Error generating API key:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to generate API key' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (sessionId.trim() && sessionId.trim().length < 20) {
      setMessage({ type: 'error', text: 'Session ID must be at least 20 characters' });
      return;
    }
    if (!sessionIdConfigured && !sessionId.trim()) {
      setMessage({ type: 'error', text: 'Session ID is required (min 20 characters)' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const payload = {
        odooBaseUrl: odooBaseUrl.trim() || 'https://foomx.odoo.com',
        externalApiTargets: externalApiTargets.map((t) => {
          const entry = {
            id: String(t.id || '').trim() || 'default',
            label: String(t.label || '').trim() || String(t.id || 'default'),
            baseUrl: String(t.baseUrl || '').trim(),
            enabled: t.enabled !== false
          };
          if (String(t.bearerTokenInput || '').trim() !== '') {
            entry.bearerToken = String(t.bearerTokenInput).trim();
          }
          return entry;
        })
      };
      if (sessionId.trim()) {
        payload.sessionId = sessionId.trim();
      }
      if (apiKey.trim() && !apiKey.includes('*')) {
        payload.apiKey = apiKey.trim();
      }
      payload.wmsApiBaseUrl = wmsApiBaseUrl.trim() || 'https://wms.foom.id/api';
      payload.wmsUsername = wmsUsername.trim();
      payload.wmsCompanyId = wmsCompanyId.trim() || 'FOOM';
      payload.wmsSite = wmsSite.trim() || 'PROD';
      if (wmsAccessTokenInput.trim() !== '') {
        payload.wmsAccessToken = wmsAccessTokenInput.trim();
      }
      const response = await axios.put('/api/admin/config', payload);

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Configuration saved successfully!' });
        setWmsAccessTokenInput('');
        await fetchConfig();
        await testConnection();
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Failed to save configuration' });
      }
    } catch (error) {
      console.error('Error saving config:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save configuration' });
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    try {
      const response = await axios.get('/api/admin/test-connection');
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Configuration saved and connection test successful!' });
      } else {
        setMessage({ type: 'warning', text: 'Configuration saved but connection test failed: ' + (response.data.error || 'Unknown error') });
      }
    } catch (error) {
      setMessage({ type: 'warning', text: 'Configuration saved but connection test failed' });
    }
  };

  const testWmsConnectionHandler = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const payload = {
        wmsApiBaseUrl: wmsApiBaseUrl.trim() || 'https://wms.foom.id/api',
        wmsUsername: wmsUsername.trim(),
        wmsCompanyId: wmsCompanyId.trim() || 'FOOM',
        wmsSite: wmsSite.trim() || 'PROD'
      };

      const token = wmsAccessTokenInput.trim();
      if (token) {
        payload.wmsAccessToken = token;
      } else if (!wmsAccessTokenConfigured) {
        setMessage({
          type: 'error',
          text: 'Masukkan WMS Access Token terlebih dahulu, lalu klik Test WMS Connection (token akan disimpan otomatis).'
        });
        return;
      }

      if (token) {
        await axios.put('/api/admin/wms-config', payload);
        setWmsAccessTokenConfigured(true);
        setWmsAccessTokenInput('');
      } else {
        await axios.put('/api/admin/wms-config', payload);
      }

      const response = await axios.post('/api/wms/test-connection', payload);
      if (response.data.success) {
        setMessage({ type: 'success', text: 'WMS connection test successful!' });
      } else {
        setMessage({ type: 'error', text: response.data.error || 'WMS connection test failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'WMS connection test failed' });
    } finally {
      setLoading(false);
    }
  };

  const handleSyncData = async () => {
    if (!window.confirm('Sync all production data to the unified table? This will copy data from liquid, device, and cartridge tables.')) {
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post('/api/admin/sync-production-data');
      if (response.data.success) {
        setSyncStats({
          synced: response.data.syncedCount || 0,
          total: response.data.totalCount || 0
        });
        setMessage({ type: 'success', text: `Sync completed! Synced ${response.data.syncedCount} records.` });
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Failed to sync data' });
      }
    } catch (error) {
      console.error('Error syncing data:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to sync data' });
    } finally {
      setLoading(false);
    }
  };

  const handlePushExternalManufacturingIdle = async () => {
    if (
      !window.confirm(
        'Register liquid MOs from cache to all enabled external manufacturing endpoints (POST idle where missing)?'
      )
    ) {
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post('/api/admin/push-external-manufacturing-idle?limit=400');
      if (response.data.success) {
        const errN = response.data.errorCount || 0;
        const lim = response.data.limitUsed != null ? response.data.limitUsed : '';
        const parts = [
          lim !== '' ? `MO rows scanned (max): ${lim}` : '',
          response.data.targetsProcessed != null ? `targets: ${response.data.targetsProcessed}` : '',
          `posted: ${response.data.posted}`,
          `skipped (already mapped): ${response.data.skipped}`,
          `linked from API: ${response.data.linkedFromRemote}`,
          errN ? `errors: ${errN}` : ''
        ]
          .filter(Boolean)
          .join(', ');
        setMessage({ type: errN ? 'warning' : 'success', text: `External manufacturing idle push completed. ${parts}` });
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Push failed' });
      }
    } catch (error) {
      console.error('Error push external manufacturing idle:', error);
      const msg =
        error.response?.status === 504
          ? 'Gateway timeout (504). Increase proxy read timeout for this upstream, or lower limit and retry.'
          : error.response?.data?.error || 'Push failed';
      setMessage({ type: 'error', text: msg });
    } finally {
      setLoading(false);
    }
  };

  const formatReconcileSummary = (data) => {
    const errN = data.errorCount || 0;
    return [
      data.dryRun ? 'preview' : 'applied',
      data.targetsProcessed != null ? `targets: ${data.targetsProcessed}` : '',
      `MES started: ${data.mesStarted != null ? data.mesStarted : 0}`,
      `would patch: ${data.wouldPatch != null ? data.wouldPatch : 0}`,
      data.dryRun ? '' : `patched: ${data.patched != null ? data.patched : 0}`,
      `already matched: ${data.alreadyMatched != null ? data.alreadyMatched : 0}`,
      `skipped active: ${data.skippedActive != null ? data.skippedActive : 0}`,
      `skipped no local: ${data.skippedNoLocal != null ? data.skippedNoLocal : 0}`,
      errN ? `errors: ${errN}` : ''
    ]
      .filter(Boolean)
      .join(', ');
  };

  const handleReconcileGatewayError = (error, fallback) => {
    if (error.response?.status === 504) {
      return 'Gateway timeout (504). Increase proxy read timeout for this upstream, or retry.';
    }
    return error.response?.data?.error || fallback;
  };

  const handlePreviewReconcileExternalFinished = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.post('/api/admin/reconcile-external-manufacturing-finished?dry_run=1', null, {
        timeout: 120000
      });
      if (response.data.success) {
        setReconcilePreview(response.data);
        setMessage({
          type: response.data.errorCount ? 'warning' : 'success',
          text: `Preview reconcile finished. ${formatReconcileSummary(response.data)}`
        });
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Preview failed' });
      }
    } catch (error) {
      console.error('Error preview reconcile external manufacturing finished:', error);
      setMessage({ type: 'error', text: handleReconcileGatewayError(error, 'Preview failed') });
    } finally {
      setLoading(false);
    }
  };

  const handleApplyReconcileExternalFinished = async () => {
    const count = reconcilePreview && reconcilePreview.wouldPatch != null ? reconcilePreview.wouldPatch : null;
    const confirmLabel =
      count != null
        ? `PATCH finished pada ${count} MO started di FOOM MES yang sudah completed di manufacturing_db?`
        : 'PATCH finished pada MO started di FOOM MES yang sudah completed di manufacturing_db?';
    if (!window.confirm(confirmLabel)) {
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.post('/api/admin/reconcile-external-manufacturing-finished?dry_run=0', null, {
        timeout: 180000
      });
      if (response.data.success) {
        setReconcilePreview(response.data);
        setMessage({
          type: response.data.errorCount ? 'warning' : 'success',
          text: `Reconcile finished applied. ${formatReconcileSummary(response.data)}`
        });
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Reconcile failed' });
      }
    } catch (error) {
      console.error('Error apply reconcile external manufacturing finished:', error);
      setMessage({ type: 'error', text: handleReconcileGatewayError(error, 'Reconcile failed') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <button onClick={() => navigate('/dashboard')} className="back-button">
          ← Back to Dashboard
        </button>
        <h1>Admin Configuration</h1>
        <button onClick={() => {
          localStorage.removeItem('authToken');
          localStorage.removeItem('isAuthenticated');
          localStorage.removeItem('userRole');
          navigate('/login');
        }} className="logout-button">
          Logout
        </button>
      </div>

      <div className="admin-content">
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        {/* Odoo Configuration Section */}
        <div className="admin-section">
          <h2>Odoo API Configuration</h2>
          <div className="form-group">
            <label>Session ID {sessionIdConfigured ? '(configured — leave blank to keep)' : '*'}</label>
            <input
              type="password"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              placeholder={sessionIdConfigured ? 'Enter new Session ID to replace' : 'Enter Odoo Session ID (min 20 characters)'}
              autoComplete="off"
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
            />
            <small style={{ color: '#94a3b8', fontSize: '12px' }}>
              {sessionIdConfigured
                ? 'Session ID is already configured. Paste a new value only when rotating.'
                : 'Session ID from Odoo authentication. Must be at least 20 characters.'}
            </small>
          </div>
          <div className="form-group">
            <label>Odoo Base URL</label>
            <input
              type="text"
              value={odooBaseUrl}
              onChange={(e) => setOdooBaseUrl(e.target.value)}
              placeholder="https://foomx.odoo.com"
              style={{ width: '100%', padding: '8px', fontSize: '14px' }}
            />
            <small style={{ color: '#94a3b8', fontSize: '12px' }}>
              Default: https://foomx.odoo.com
            </small>
          </div>
          <div className="form-group">
            <label>External manufacturing API endpoints (produksi liquid)</label>
            <small style={{ color: '#94a3b8', fontSize: '12px', display: 'block', marginBottom: '12px' }}>
              Setiap endpoint memakai path tetap <code>/api/v1/manufacturing</code> untuk POST/PUT/PATCH sinkron MO liquid
              (confirm input, submit MO, idle push). Device/cartridge tidak memakai konfigurasi ini. Id target harus unik
              (dipakai di map UUID lokal).
            </small>
            {externalApiTargets.map((target, index) => (
              <div
                key={target.id + '-' + index}
                style={{
                  border: '1px solid #334155',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '12px',
                  background: '#0f172a'
                }}
              >
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '8px', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#cbd5e1' }}>
                    <input
                      type="checkbox"
                      checked={target.enabled !== false}
                      onChange={(e) => {
                        const next = [...externalApiTargets];
                        next[index] = { ...next[index], enabled: e.target.checked };
                        setExternalApiTargets(next);
                      }}
                    />
                    Enabled
                  </label>
                  {externalApiTargets.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setExternalApiTargets(externalApiTargets.filter((_, i) => i !== index))}
                      style={{
                        marginLeft: 'auto',
                        background: 'transparent',
                        border: '1px solid #7f1d1d',
                        color: '#fca5a5',
                        borderRadius: '6px',
                        padding: '4px 10px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                  <div>
                    <label style={{ fontSize: '12px', color: '#94a3b8' }}>Id (stabil)</label>
                    <input
                      type="text"
                      value={target.id}
                      onChange={(e) => {
                        const next = [...externalApiTargets];
                        next[index] = { ...next[index], id: e.target.value };
                        setExternalApiTargets(next);
                      }}
                      placeholder="default"
                      style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#94a3b8' }}>Label</label>
                    <input
                      type="text"
                      value={target.label}
                      onChange={(e) => {
                        const next = [...externalApiTargets];
                        next[index] = { ...next[index], label: e.target.value };
                        setExternalApiTargets(next);
                      }}
                      placeholder="FOOM primary"
                      style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                    />
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: '8px' }}>
                  <label style={{ fontSize: '12px', color: '#94a3b8' }}>Base URL</label>
                  <input
                    type="text"
                    value={target.baseUrl}
                    onChange={(e) => {
                      const next = [...externalApiTargets];
                      next[index] = { ...next[index], baseUrl: e.target.value };
                      setExternalApiTargets(next);
                    }}
                    placeholder="http://127.0.0.1:8083"
                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: '12px', color: '#94a3b8' }}>Bearer token</label>
                  {target.bearerTokenConfigured && (
                    <p style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '6px' }}>
                      Token tersimpan. Isi field hanya jika ingin mengganti.
                    </p>
                  )}
                  <input
                    type="password"
                    value={target.bearerTokenInput}
                    onChange={(e) => {
                      const next = [...externalApiTargets];
                      next[index] = { ...next[index], bearerTokenInput: e.target.value };
                      setExternalApiTargets(next);
                    }}
                    placeholder={target.bearerTokenConfigured ? '•••••••• (kosongkan jika tidak diubah)' : 'Bearer token'}
                    autoComplete="off"
                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                const n = externalApiTargets.length + 1;
                setExternalApiTargets([
                  ...externalApiTargets,
                  {
                    id: `target-${n}`,
                    label: `Endpoint ${n}`,
                    baseUrl: '',
                    bearerTokenInput: '',
                    bearerTokenConfigured: false,
                    enabled: true
                  }
                ]);
              }}
              style={{
                background: '#1e293b',
                border: '1px solid #475569',
                color: '#e2e8f0',
                borderRadius: '6px',
                padding: '8px 14px',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              Add endpoint
            </button>
          </div>

          <div className="form-group" style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #334155' }}>
            <label style={{ fontSize: '16px', fontWeight: '600', color: '#e2e8f0', marginBottom: '12px' }}>
              WMS Prieds Configuration
            </label>
            <div className="form-group">
              <label>WMS API Base URL</label>
              <input
                type="text"
                value={wmsApiBaseUrl}
                onChange={(e) => setWmsApiBaseUrl(e.target.value)}
                placeholder="https://wms.foom.id/api"
                style={{ width: '100%', padding: '8px', fontSize: '14px' }}
              />
            </div>
            <div className="form-group">
              <label>WMS Access Token (JWT)</label>
              {wmsAccessTokenConfigured && (
                <p style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '8px' }}>
                  Token tersimpan. Isi field di bawah hanya jika ingin mengganti token.
                </p>
              )}
              <input
                type="password"
                value={wmsAccessTokenInput}
                onChange={(e) => setWmsAccessTokenInput(e.target.value)}
                placeholder={wmsAccessTokenConfigured ? '•••••••• (kosongkan jika tidak diubah)' : 'JWT x-access-token dari Prieds WMS'}
                autoComplete="off"
                style={{ width: '100%', padding: '8px', fontSize: '14px' }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={wmsUsername}
                  onChange={(e) => setWmsUsername(e.target.value)}
                  placeholder="puput"
                  style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                />
              </div>
              <div className="form-group">
                <label>Company ID</label>
                <input
                  type="text"
                  value={wmsCompanyId}
                  onChange={(e) => setWmsCompanyId(e.target.value)}
                  placeholder="FOOM"
                  style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                />
              </div>
              <div className="form-group">
                <label>Site</label>
                <input
                  type="text"
                  value={wmsSite}
                  onChange={(e) => setWmsSite(e.target.value)}
                  placeholder="PROD"
                  style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={testWmsConnectionHandler}
              disabled={loading}
              style={{
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: '600',
                background: '#334155',
                color: '#e2e8f0',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                marginTop: '8px'
              }}
            >
              Test WMS Connection
            </button>
            <small style={{ color: '#94a3b8', fontSize: '12px', display: 'block', marginTop: '8px' }}>
              Token dari field di atas akan disimpan otomatis saat test. Tidak perlu klik Save Configuration terlebih dahulu.
            </small>
          </div>

          {/* API Key Configuration */}
          <div className="form-group" style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #334155' }}>
            <label style={{ fontSize: '16px', fontWeight: '600', color: '#e2e8f0', marginBottom: '12px' }}>
              API Key Authentication
            </label>
            <div style={{ 
              background: '#1e293b', 
              padding: '16px', 
              borderRadius: '6px', 
              border: '1px solid #334155',
              marginBottom: '12px'
            }}>
              {apiKeyConfigured ? (
                <div>
                  <p style={{ color: '#94a3b8', marginBottom: '12px', fontSize: '14px' }}>
                    ✅ API Key sudah dikonfigurasi
                  </p>
                  <div style={{ 
                    background: '#0f172a', 
                    padding: '12px', 
                    borderRadius: '4px', 
                    border: '1px solid #334155',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    color: '#60a5fa',
                    wordBreak: 'break-all',
                    marginBottom: '12px'
                  }}>
                    {apiKey || 'Not configured'}
                  </div>
                  <small style={{ color: '#94a3b8', fontSize: '12px', display: 'block', marginBottom: '12px' }}>
                    API key ini digunakan untuk autentikasi pada endpoint external API. 
                    Gunakan header <code style={{ background: '#0f172a', padding: '2px 6px', borderRadius: '3px', color: '#60a5fa' }}>X-API-Key</code> saat memanggil API.
                  </small>
                </div>
              ) : (
                <div>
                  <p style={{ color: '#fbbf24', marginBottom: '12px', fontSize: '14px' }}>
                    ⚠️ API Key belum dikonfigurasi. External API endpoints saat ini dapat diakses tanpa autentikasi.
                  </p>
                  <small style={{ color: '#94a3b8', fontSize: '12px', display: 'block', marginBottom: '12px' }}>
                    Generate API key untuk mengamankan endpoint external API. Setelah di-generate, semua request ke external API harus menyertakan API key.
                  </small>
                </div>
              )}
              
              {showApiKey && generatedApiKey && (
                <div style={{ 
                  background: '#065f46', 
                  padding: '16px', 
                  borderRadius: '6px', 
                  border: '2px solid #10b981',
                  marginBottom: '12px'
                }}>
                  <p style={{ color: '#10b981', fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>
                    ⚠️ IMPORTANT: Save this API key now!
                  </p>
                  <div style={{ 
                    background: '#0f172a', 
                    padding: '12px', 
                    borderRadius: '4px', 
                    border: '1px solid #10b981',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    color: '#34d399',
                    wordBreak: 'break-all',
                    marginBottom: '12px',
                    fontWeight: '600'
                  }}>
                    {generatedApiKey}
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(generatedApiKey);
                      setMessage({ type: 'success', text: 'API key copied to clipboard!' });
                    }}
                    style={{ 
                      padding: '8px 16px', 
                      fontSize: '13px', 
                      fontWeight: '600',
                      background: '#10b981',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      marginRight: '8px'
                    }}
                  >
                    Copy to Clipboard
                  </button>
                  <button
                    onClick={() => {
                      setShowApiKey(false);
                      setGeneratedApiKey('');
                    }}
                    style={{ 
                      padding: '8px 16px', 
                      fontSize: '13px', 
                      fontWeight: '600',
                      background: '#334155',
                      color: '#e2e8f0',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Hide
                  </button>
                </div>
              )}
              
              <button
                onClick={handleGenerateApiKey}
                disabled={loading}
                style={{ 
                  padding: '10px 20px', 
                  fontSize: '14px', 
                  fontWeight: '600',
                  background: '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Generating...' : apiKeyConfigured ? 'Regenerate API Key' : 'Generate API Key'}
              </button>
            </div>
          </div>
          
          <button
            onClick={handleSaveConfig}
            disabled={loading}
            className="save-button"
            style={{ padding: '10px 20px', fontSize: '14px', fontWeight: '600', marginTop: '16px' }}
          >
            {loading ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>

        <div className="admin-section">
          <h2>Sinkronisasi MO Odoo</h2>
          <p style={{ color: '#94a3b8', marginBottom: '12px' }}>
            Toggle pull otomatis, interval (default 3 menit), statistik cache, sync manual, dan cleanup ada di halaman terpisah.
          </p>
          <button
            type="button"
            onClick={() => navigate('/admin/odoo-mo-sync')}
            className="sync-button"
            style={{ padding: '10px 20px', fontSize: '14px', fontWeight: '600' }}
          >
            Buka konfigurasi sinkronisasi MO Odoo
          </button>
        </div>

        {/* Data Sync Section */}
        <div className="admin-section">
          <h2>Production Data Sync</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">Synced Records</div>
              <div className="stat-value">{syncStats.synced}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Records</div>
              <div className="stat-value">{syncStats.total}</div>
            </div>
          </div>
          <button
            onClick={handleSyncData}
            disabled={loading}
            className="sync-button"
            style={{ padding: '10px 20px', fontSize: '14px', fontWeight: '600', marginTop: '16px' }}
          >
            {loading ? 'Syncing...' : 'Sync Production Data'}
          </button>
          <small style={{ color: '#94a3b8', fontSize: '12px', display: 'block', marginTop: '8px' }}>
            Sync all data from Production Liquid, Device, and Cartridge tables to the unified production_results table.
          </small>
          <button
            onClick={handlePushExternalManufacturingIdle}
            disabled={loading}
            type="button"
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              marginTop: '12px',
              display: 'block',
              background: '#0d9488',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Processing...' : 'Push external manufacturing (idle, liquid MOs)'}
          </button>
          <small style={{ color: '#94a3b8', fontSize: '12px', display: 'block', marginTop: '8px' }}>
            Same job as the daily 06:00 scheduler: one FOOM list prefetch per run, then up to 400 eligible liquid MO rows from cache per click (<code style={{ background: '#1e293b', padding: '2px 6px', borderRadius: '4px' }}>POST /api/admin/push-external-manufacturing-idle</code>). Eligible when <code style={{ background: '#1e293b', padding: '2px 6px', borderRadius: '4px' }}>team_name</code> is LIQ… or G1/G2/…, or note is TEAM/TIM LIQUID SHIFT; excluding SKU MIXING/BRAY/bundling/15 ML and cartridge/device notes; <code style={{ background: '#1e293b', padding: '2px 6px', borderRadius: '4px' }}>create_date</code> within ±6 days (not before 2026-07-01). Cross-checks <code style={{ background: '#1e293b', padding: '2px 6px', borderRadius: '4px' }}>external_manufacturing_map</code>, then POST idle when needed.
          </small>
          <button
            onClick={handlePreviewReconcileExternalFinished}
            disabled={loading}
            type="button"
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              marginTop: '16px',
              display: 'block',
              background: '#1d4ed8',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Processing...' : 'Preview reconcile MES finished'}
          </button>
          <button
            onClick={handleApplyReconcileExternalFinished}
            disabled={loading}
            type="button"
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              marginTop: '8px',
              display: 'block',
              background: '#b45309',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Processing...' : 'Apply reconcile MES finished'}
          </button>
          <small style={{ color: '#94a3b8', fontSize: '12px', display: 'block', marginTop: '8px' }}>
            Menambal MO yang di FOOM MES masih <code style={{ background: '#1e293b', padding: '2px 6px', borderRadius: '4px' }}>started</code> padahal di <code style={{ background: '#1e293b', padding: '2px 6px', borderRadius: '4px' }}>production_liquid</code> sudah completed. Preview dulu, lalu Apply untuk PUT+PATCH finished (plus done_qty). MO yang sudah finished/completed atau masih active dibiarkan. (<code style={{ background: '#1e293b', padding: '2px 6px', borderRadius: '4px' }}>POST /api/admin/reconcile-external-manufacturing-finished</code>)
          </small>
          {reconcilePreview && Array.isArray(reconcilePreview.candidates) && reconcilePreview.candidates.length > 0 && (
            <div style={{ marginTop: '12px', overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse', color: '#cbd5e1' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '6px', borderBottom: '1px solid #334155' }}>MO</th>
                    <th style={{ textAlign: 'left', padding: '6px', borderBottom: '1px solid #334155' }}>record_id</th>
                    <th style={{ textAlign: 'right', padding: '6px', borderBottom: '1px solid #334155' }}>done_qty</th>
                    <th style={{ textAlign: 'right', padding: '6px', borderBottom: '1px solid #334155' }}>target_qty</th>
                  </tr>
                </thead>
                <tbody>
                  {reconcilePreview.candidates.map((row) => (
                    <tr key={`${row.target || 'default'}-${row.manufacturing_id}-${row.record_id}`}>
                      <td style={{ padding: '6px', borderBottom: '1px solid #1e293b' }}>{row.manufacturing_id}</td>
                      <td style={{ padding: '6px', borderBottom: '1px solid #1e293b', fontFamily: 'monospace' }}>{row.record_id}</td>
                      <td style={{ padding: '6px', borderBottom: '1px solid #1e293b', textAlign: 'right' }}>{row.done_qty}</td>
                      <td style={{ padding: '6px', borderBottom: '1px solid #1e293b', textAlign: 'right' }}>{row.target_qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* External API Integration Documentation */}
        <div className="admin-section">
          <h2>External API Integration Documentation</h2>
          <div style={{ 
            background: '#0f172a', 
            padding: '24px', 
            borderRadius: '8px', 
            border: '1px solid #334155',
            marginTop: '16px'
          }}>
            <h3 style={{ marginTop: '0', color: '#60a5fa', fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>Integrasi API Eksternal untuk Produksi Liquid</h3>
            
            <div style={{ marginTop: '24px', paddingBottom: '24px', borderBottom: '1px solid #334155' }}>
              <h4 style={{ color: '#34d399', marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>1. Pengiriman Data MO List (Scheduler)</h4>
              <p style={{ marginBottom: '12px', lineHeight: '1.6', color: '#cbd5e1' }}>
                Sistem secara otomatis mengirimkan daftar MO (Manufacturing Order) produksi liquid ke API eksternal setiap 6 jam melalui scheduler.
              </p>
              <p style={{ marginBottom: '8px', lineHeight: '1.6', color: '#cbd5e1' }}>
                <strong style={{ color: '#e2e8f0' }}>Data yang dikirim:</strong>
              </p>
              <ul style={{ marginLeft: '24px', marginBottom: '12px', lineHeight: '1.8', color: '#cbd5e1' }}>
                <li>MO number (string)</li>
                <li>SKU name (string)</li>
                <li>Target qty (integer)</li>
              </ul>
              <p style={{ marginBottom: '10px', lineHeight: '1.6', color: '#cbd5e1' }}>
                <strong style={{ color: '#e2e8f0' }}>Format pengiriman:</strong> Array dengan key <code style={{ background: '#1e293b', padding: '2px 8px', borderRadius: '4px', color: '#60a5fa', border: '1px solid #334155' }}>mo_list</code>
              </p>
              <div style={{ 
                background: '#1e293b', 
                padding: '16px', 
                borderRadius: '6px', 
                border: '1px solid #334155',
                marginTop: '12px',
                fontFamily: 'monospace',
                fontSize: '13px',
                color: '#a5b4fc',
                overflowX: 'auto'
              }}>
                {`{
  "mo_list": [
    {
      "mo": "MO001",
      "sku": "SKU001",
      "target_qty": 100
    }
  ]
}`}
              </div>
            </div>

             <div style={{ marginTop: '24px', paddingBottom: '24px', borderBottom: '1px solid #334155' }}>
               <h4 style={{ color: '#34d399', marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>2. Pengiriman Data Input Authenticity Label Process</h4>
               <p style={{ marginBottom: '12px', lineHeight: '1.6', color: '#cbd5e1' }}>
                 Setiap kali melakukan Input Authenticity Label Process pada produksi liquid, sistem akan mengirimkan data ke API eksternal dengan status <strong style={{ color: '#34d399' }}>"active"</strong> menggunakan <strong style={{ color: '#60a5fa' }}>External API URL - Active Status</strong>.
               </p>
               <p style={{ marginBottom: '8px', lineHeight: '1.6', color: '#cbd5e1' }}>
                 <strong style={{ color: '#e2e8f0' }}>Format data yang dikirim:</strong>
               </p>
               <div style={{ 
                 background: '#1e293b', 
                 padding: '16px', 
                 borderRadius: '6px', 
                 border: '1px solid #334155',
                 marginTop: '12px',
                 fontFamily: 'monospace',
                 fontSize: '13px',
                 color: '#a5b4fc',
                 overflowX: 'auto'
               }}>
                 {`{
   "status": "active"
 }`}
               </div>
             </div>

             <div style={{ marginTop: '24px', paddingBottom: '24px', borderBottom: '1px solid #334155' }}>
               <h4 style={{ color: '#34d399', marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>3. Pengiriman Status Completed</h4>
               <p style={{ marginBottom: '12px', lineHeight: '1.6', color: '#cbd5e1' }}>
                 Ketika MO number disubmit (semua input untuk MO tersebut sudah completed), sistem akan mengirimkan data dengan status <strong style={{ color: '#34d399' }}>"completed"</strong> menggunakan <strong style={{ color: '#60a5fa' }}>External API URL - Completed Status</strong>.
               </p>
               <p style={{ marginBottom: '8px', lineHeight: '1.6', color: '#cbd5e1' }}>
                 <strong style={{ color: '#e2e8f0' }}>Format data yang dikirim:</strong>
               </p>
               <div style={{ 
                 background: '#1e293b', 
                 padding: '16px', 
                 borderRadius: '6px', 
                 border: '1px solid #334155',
                 marginTop: '12px',
                 fontFamily: 'monospace',
                 fontSize: '13px',
                 color: '#a5b4fc',
                 overflowX: 'auto'
               }}>
                 {`{
   "status": "completed"
 }`}
               </div>
             </div>

            <div style={{ marginTop: '24px', paddingBottom: '24px', borderBottom: '1px solid #334155' }}>
              <h4 style={{ color: '#34d399', marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>4. Endpoint untuk Mengecek Status MO</h4>
              <p style={{ marginBottom: '12px', lineHeight: '1.6', color: '#cbd5e1' }}>
                Endpoint ini digunakan untuk mengecek status MO (active atau completed) berdasarkan data yang ada di database.
              </p>
              <p style={{ marginBottom: '8px', lineHeight: '1.6', color: '#cbd5e1' }}>
                <strong style={{ color: '#e2e8f0' }}>Endpoint:</strong>
              </p>
              <div style={{ 
                background: '#1e293b', 
                padding: '16px', 
                borderRadius: '6px', 
                border: '1px solid #334155',
                marginTop: '12px',
                marginBottom: '12px',
                fontFamily: 'monospace',
                fontSize: '13px',
                color: '#a5b4fc',
                overflowX: 'auto'
              }}>
                {`GET /api/external/manufacturing-data/status?mo_number=PROD/MO/28246&completed_at=all`}
              </div>
              <p style={{ marginBottom: '8px', lineHeight: '1.6', color: '#cbd5e1' }}>
                <strong style={{ color: '#e2e8f0' }}>Parameter:</strong>
              </p>
              <ul style={{ marginLeft: '24px', marginBottom: '12px', lineHeight: '1.8', color: '#cbd5e1' }}>
                <li><strong style={{ color: '#e2e8f0' }}>mo_number</strong> (required): Nomor MO yang ingin dicek statusnya</li>
                <li><strong style={{ color: '#e2e8f0' }}>completed_at</strong> (optional): Filter berdasarkan tanggal completed (atau "all" untuk semua)</li>
              </ul>
              <p style={{ marginBottom: '8px', lineHeight: '1.6', color: '#cbd5e1' }}>
                <strong style={{ color: '#e2e8f0' }}>Response jika ada input dengan status "active":</strong>
              </p>
              <div style={{ 
                background: '#1e293b', 
                padding: '16px', 
                borderRadius: '6px', 
                border: '1px solid #334155',
                marginTop: '12px',
                marginBottom: '12px',
                fontFamily: 'monospace',
                fontSize: '13px',
                color: '#a5b4fc',
                overflowX: 'auto'
              }}>
                {`{
  "status": "active"
}`}
              </div>
              <p style={{ marginBottom: '8px', lineHeight: '1.6', color: '#cbd5e1' }}>
                <strong style={{ color: '#e2e8f0' }}>Response jika semua input sudah "completed":</strong>
              </p>
              <div style={{ 
                background: '#1e293b', 
                padding: '16px', 
                borderRadius: '6px', 
                border: '1px solid #334155',
                marginTop: '12px',
                marginBottom: '12px',
                fontFamily: 'monospace',
                fontSize: '13px',
                color: '#a5b4fc',
                overflowX: 'auto'
              }}>
                {`{
  "status": "completed"
}`}
              </div>
              <p style={{ marginBottom: '8px', lineHeight: '1.6', color: '#cbd5e1' }}>
                <strong style={{ color: '#e2e8f0' }}>Response jika MO number tidak ditemukan:</strong>
              </p>
              <div style={{ 
                background: '#1e293b', 
                padding: '16px', 
                borderRadius: '6px', 
                border: '1px solid #334155',
                marginTop: '12px',
                fontFamily: 'monospace',
                fontSize: '13px',
                color: '#f87171',
                overflowX: 'auto'
              }}>
                {`{
  "success": false,
  "error": "MO number not found",
  "status": null
}`}
              </div>
              <p style={{ marginBottom: '8px', marginTop: '12px', lineHeight: '1.6', color: '#cbd5e1' }}>
                <strong style={{ color: '#e2e8f0' }}>Contoh penggunaan dengan curl:</strong>
              </p>
              <div style={{ 
                background: '#1e293b', 
                padding: '16px', 
                borderRadius: '6px', 
                border: '1px solid #334155',
                marginTop: '12px',
                fontFamily: 'monospace',
                fontSize: '13px',
                color: '#a5b4fc',
                overflowX: 'auto'
              }}>
                {`curl.exe -X GET "http://localhost:3000/api/external/manufacturing-data/status?mo_number=PROD/MO/28246&completed_at=all"`}
              </div>
              <p style={{ marginBottom: '0', marginTop: '12px', lineHeight: '1.6', color: '#cbd5e1' }}>
                <strong style={{ color: '#e2e8f0' }}>Logika:</strong> Endpoint ini mengecek semua tabel production (liquid, device, cartridge) untuk menentukan status MO. Jika ada input dengan status "active", maka status MO adalah "active". Jika semua input sudah "completed" (tidak ada yang "active"), maka status MO adalah "completed".
              </p>
            </div>

            <div style={{ marginTop: '24px' }}>
              <h4 style={{ color: '#f87171', marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>Catatan Penting:</h4>
              <ul style={{ marginLeft: '24px', lineHeight: '1.8', color: '#cbd5e1' }}>
                <li style={{ marginBottom: '8px' }}>Pengiriman data dilakukan secara asinkron (tidak menghambat proses utama)</li>
                <li style={{ marginBottom: '8px' }}>Jika pengiriman gagal, error akan dicatat di log server tetapi tidak akan mengganggu proses produksi</li>
                <li style={{ marginBottom: '8px' }}>URL API eksternal dapat dikonfigurasi di bagian "Odoo API Configuration" di atas dengan 2 URL terpisah:
                  <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                    <li style={{ marginBottom: '4px' }}><strong style={{ color: '#e2e8f0' }}>External API URL - Active Status:</strong> Untuk pengiriman saat Input Authenticity Label Process</li>
                    <li><strong style={{ color: '#e2e8f0' }}>External API URL - Completed Status:</strong> Untuk pengiriman saat MO disubmit</li>
                  </ul>
                </li>
                <li style={{ marginBottom: '8px' }}>Jika URL spesifik tidak dikonfigurasi, sistem akan menggunakan <strong style={{ color: '#e2e8f0' }}>External API URL (Fallback/General)</strong> sebagai fallback</li>
                <li style={{ marginBottom: '8px' }}>Default URL: <code style={{ background: '#1e293b', padding: '2px 8px', borderRadius: '4px', color: '#60a5fa', border: '1px solid #334155' }}>https://foom-dash.vercel.app/API</code></li>
                <li>Scheduler untuk pengiriman MO list berjalan setiap 6 jam (10 menit setelah update MO data) dan menggunakan URL fallback</li>
              </ul>
            </div>
          </div>
        </div>

        {/* PIC Management Section */}
        <div className="config-section" style={{ marginTop: '24px' }}>
          <h2>📋 PIC (Person in Charge) Management</h2>
          <p className="section-description">
            Kelola daftar PIC yang dapat dipilih pada form Input Authenticity. PIC yang aktif akan muncul di dropdown.
          </p>

          {picMessage.text && (
            <div className={`message ${picMessage.type}`} style={{ marginBottom: '16px' }}>
              {picMessage.text}
            </div>
          )}

          {/* Add New PIC */}
          <div className="pic-add-section" style={{ marginBottom: '24px', padding: '16px', background: '#1e293b', borderRadius: '8px', border: '1px solid #334155' }}>
            <h3 style={{ color: '#e2e8f0', fontSize: '16px', marginBottom: '12px' }}>Tambah PIC Baru</h3>
            <div style={{ display: 'flex', gap: '12px' }}>
              <input
                type="text"
                value={newPicName}
                onChange={(e) => setNewPicName(e.target.value)}
                placeholder="Masukkan nama PIC"
                onKeyPress={(e) => e.key === 'Enter' && handleAddPic()}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  background: '#0f172a',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#e2e8f0',
                  fontSize: '14px'
                }}
              />
              <button 
                onClick={handleAddPic}
                style={{
                  padding: '10px 24px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Tambah
              </button>
            </div>
          </div>

          {/* PIC List */}
          <div className="pic-list-section">
            <h3 style={{ color: '#e2e8f0', fontSize: '16px', marginBottom: '12px' }}>Daftar PIC ({picList.length})</h3>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {picList.length === 0 ? (
                <p style={{ color: '#94a3b8', textAlign: 'center', padding: '24px' }}>Tidak ada PIC</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#1e293b', zIndex: 1 }}>
                    <tr style={{ borderBottom: '2px solid #334155' }}>
                      <th style={{ padding: '12px', textAlign: 'left', color: '#e2e8f0', fontWeight: '600', fontSize: '14px' }}>No</th>
                      <th style={{ padding: '12px', textAlign: 'left', color: '#e2e8f0', fontWeight: '600', fontSize: '14px' }}>Nama PIC</th>
                      <th style={{ padding: '12px', textAlign: 'center', color: '#e2e8f0', fontWeight: '600', fontSize: '14px' }}>Status</th>
                      <th style={{ padding: '12px', textAlign: 'center', color: '#e2e8f0', fontWeight: '600', fontSize: '14px' }}>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {picList.map((pic, index) => (
                      <tr key={pic.id} style={{ borderBottom: '1px solid #334155' }}>
                        <td style={{ padding: '12px', color: '#cbd5e1', fontSize: '14px' }}>{index + 1}</td>
                        <td style={{ padding: '12px', color: '#e2e8f0', fontSize: '14px' }}>
                          {editingPic === pic.id ? (
                            <input
                              type="text"
                              defaultValue={pic.name}
                              onBlur={(e) => {
                                if (e.target.value.trim() && e.target.value !== pic.name) {
                                  handleUpdatePic(pic.id, e.target.value.trim(), pic.is_active);
                                } else {
                                  setEditingPic(null);
                                }
                              }}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  if (e.target.value.trim() && e.target.value !== pic.name) {
                                    handleUpdatePic(pic.id, e.target.value.trim(), pic.is_active);
                                  } else {
                                    setEditingPic(null);
                                  }
                                }
                              }}
                              autoFocus
                              style={{
                                padding: '6px 10px',
                                background: '#0f172a',
                                border: '1px solid #3b82f6',
                                borderRadius: '4px',
                                color: '#e2e8f0',
                                fontSize: '14px',
                                width: '100%'
                              }}
                            />
                          ) : (
                            pic.name
                          )}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background: pic.is_active === 1 ? '#10b98180' : '#6b728080',
                            color: pic.is_active === 1 ? '#10b981' : '#9ca3af',
                            border: `1px solid ${pic.is_active === 1 ? '#10b981' : '#6b7280'}`
                          }}>
                            {pic.is_active === 1 ? 'Aktif' : 'Nonaktif'}
                          </span>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            {editingPic !== pic.id && (
                              <button
                                onClick={() => setEditingPic(pic.id)}
                                style={{
                                  padding: '6px 12px',
                                  background: '#3b82f6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  cursor: 'pointer'
                                }}
                              >
                                Edit
                              </button>
                            )}
                            <button
                              onClick={() => handleTogglePicStatus(pic.id, pic.is_active)}
                              style={{
                                padding: '6px 12px',
                                background: pic.is_active === 1 ? '#ef4444' : '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              {pic.is_active === 1 ? 'Nonaktifkan' : 'Aktifkan'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div style={{ marginTop: '16px', padding: '12px', background: '#1e293b', borderRadius: '6px', border: '1px solid #334155' }}>
            <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0, lineHeight: '1.6' }}>
              <strong style={{ color: '#e2e8f0' }}>📌 Catatan:</strong> PIC yang dinonaktifkan tidak akan muncul di dropdown form Input Authenticity, tetapi data lama yang sudah menggunakan PIC tersebut tetap akan tersimpan.
            </p>
          </div>
        </div>

        <div className="config-section vendor-admin-section" style={{ marginTop: '24px' }}>
          <h2>Vendor authenticity (nama + digit)</h2>
          <p className="section-description">
            Atur vendor untuk validasi panjang digit pada First/Last authenticity dan nomor buffer/reject di halaman produksi Liquid, Device, dan Cartridge.
          </p>
          {vendorMessage.text && (
            <div className={`message ${vendorMessage.type}`} style={{ marginBottom: '16px' }}>
              {vendorMessage.text}
            </div>
          )}
          <div className="vendor-add-card" style={{ marginBottom: '24px', padding: '16px', background: '#1e293b', borderRadius: '8px', border: '1px solid #334155' }}>
            <h3 style={{ color: '#e2e8f0', fontSize: '16px', marginBottom: '12px' }}>Tambah vendor</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Nama vendor</label>
                <input
                  type="text"
                  value={newVendorName}
                  onChange={(e) => setNewVendorName(e.target.value)}
                  placeholder="Contoh: Vendor A"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: '#0f172a',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#e2e8f0',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ width: '140px' }}>
                <label style={{ display: 'block', color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Digit</label>
                <input
                  type="number"
                  min={1}
                  value={newVendorDigitCount}
                  onChange={(e) => setNewVendorDigitCount(e.target.value)}
                  placeholder="12"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: '#0f172a',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#e2e8f0',
                    fontSize: '14px'
                  }}
                />
              </div>
              <button
                type="button"
                onClick={handleAddVendor}
                style={{
                  padding: '10px 24px',
                  background: '#6366f1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Tambah
              </button>
            </div>
          </div>
          <div className="vendor-list-section">
            <h3 style={{ color: '#e2e8f0', fontSize: '16px', marginBottom: '12px' }}>Daftar vendor ({vendorList.length})</h3>
            <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
              {vendorList.length === 0 ? (
                <p style={{ color: '#94a3b8', textAlign: 'center', padding: '24px' }}>Belum ada vendor</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#1e293b', zIndex: 1 }}>
                    <tr style={{ borderBottom: '2px solid #334155' }}>
                      <th style={{ padding: '12px', textAlign: 'left', color: '#e2e8f0', fontSize: '14px' }}>Nama</th>
                      <th style={{ padding: '12px', textAlign: 'center', color: '#e2e8f0', fontSize: '14px' }}>Digit</th>
                      <th style={{ padding: '12px', textAlign: 'center', color: '#e2e8f0', fontSize: '14px' }}>Status</th>
                      <th style={{ padding: '12px', textAlign: 'center', color: '#e2e8f0', fontSize: '14px' }}>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendorList.map((v) => (
                      <tr key={v.id} style={{ borderBottom: '1px solid #334155' }}>
                        <td style={{ padding: '12px', color: '#e2e8f0', fontSize: '14px' }}>
                          {editingVendor === v.id ? (
                            <input
                              type="text"
                              defaultValue={v.name}
                              id={`vendor-name-edit-${v.id}`}
                              style={{
                                padding: '6px 10px',
                                background: '#0f172a',
                                border: '1px solid #6366f1',
                                borderRadius: '4px',
                                color: '#e2e8f0',
                                width: '100%'
                              }}
                            />
                          ) : (
                            v.name
                          )}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center', color: '#cbd5e1' }}>
                          {editingVendor === v.id ? (
                            <input
                              type="number"
                              min={1}
                              defaultValue={v.digit_count}
                              id={`vendor-digit-edit-${v.id}`}
                              style={{
                                width: '80px',
                                padding: '6px 10px',
                                background: '#0f172a',
                                border: '1px solid #6366f1',
                                borderRadius: '4px',
                                color: '#e2e8f0'
                              }}
                            />
                          ) : (
                            v.digit_count
                          )}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <span
                            style={{
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '600',
                              background: v.is_active === 1 ? '#10b98140' : '#6b728040',
                              color: v.is_active === 1 ? '#34d399' : '#9ca3af'
                            }}
                          >
                            {v.is_active === 1 ? 'Aktif' : 'Nonaktif'}
                          </span>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {editingVendor === v.id ? (
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                onClick={() => {
                                  const nameEl = document.getElementById(`vendor-name-edit-${v.id}`);
                                  const digitEl = document.getElementById(`vendor-digit-edit-${v.id}`);
                                  const name = nameEl ? nameEl.value.trim() : '';
                                  const dc = digitEl ? parseInt(digitEl.value, 10) : NaN;
                                  if (!name) {
                                    setVendorMessage({ type: 'error', text: 'Nama vendor wajib' });
                                    return;
                                  }
                                  if (Number.isNaN(dc) || dc < 1) {
                                    setVendorMessage({ type: 'error', text: 'Digit tidak valid' });
                                    return;
                                  }
                                  handleUpdateVendor(v.id, name, dc, v.is_active);
                                }}
                                style={{
                                  padding: '6px 12px',
                                  background: '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  cursor: 'pointer'
                                }}
                              >
                                Simpan
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingVendor(null)}
                                style={{
                                  padding: '6px 12px',
                                  background: '#475569',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  cursor: 'pointer'
                                }}
                              >
                                Batal
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                onClick={() => setEditingVendor(v.id)}
                                style={{
                                  padding: '6px 12px',
                                  background: '#3b82f6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  cursor: 'pointer'
                                }}
                              >
                                Edit
                              </button>
                              {v.is_active === 1 && (
                                <button
                                  type="button"
                                  onClick={() => handleDeactivateVendor(v.id)}
                                  style={{
                                    padding: '6px 12px',
                                    background: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Nonaktifkan
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Admin;

